/**
 * Iter XX.111 — SessionMemoryCard.
 *
 * Rendered beneath the assistant message on the EXACT turn a
 * coaching arc closes, before any Plus offer appears on the next
 * turn. Founder principle:
 *
 *   "A single conversation helps a player understand today's
 *    session. A season of conversations helps a player understand
 *    themselves."
 *
 * The card communicates that idea through the coaching experience
 * itself — never as marketing copy.
 *
 * Contract:
 *   memory = {
 *     saved: true,
 *     observation: "You became more aggressive after your first boundary.",
 *     category: "match" | "practice" | "mindset" | "technique",
 *     confidence_label: "low",
 *     confidence_line: "Low — this is based on one session.",
 *     confidence_note: "I'll become more accurate as we review more of your cricket together.",
 *     journal_preview: [
 *       {key, status: "saved" | "locked", title: "..."},
 *       ...
 *     ],
 *   }
 *
 * Guardrails:
 *   · No CTA, no "Upgrade" button — the Plus surface stays on its
 *     own turn.
 *   · Journal preview items are static previews, NOT clickable.
 *   · The confidence line is always "low" for the first session;
 *     never over-claim.
 */
import React, { useEffect, useRef } from "react";
import { Check, Lock } from "lucide-react";

export default function SessionMemoryCard({ memory }) {
  const shownFiredRef = useRef(false);

  const isPlusPreview = !!(memory && memory.plus_preview);

  // Iter XX.285h — Frequency cap: max once per user/day.
  // The "Today's session saved" beat should reinforce continuity,
  // not nag. Check localStorage before rendering.
  const todayKey = (() => {
    try {
      const d = new Date();
      return `gully_session_saved_${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    } catch { return null; }
  })();
  const alreadyShownToday = (() => {
    try {
      return todayKey && localStorage.getItem(todayKey) === "1";
    } catch { return false; }
  })();
  useEffect(() => {
    if (alreadyShownToday) return;
    if (shownFiredRef.current) return;
    shownFiredRef.current = true;
    // Iter XX.285h — Mark as shown today for frequency capping.
    try {
      if (todayKey) localStorage.setItem(todayKey, "1");
    } catch { /* noop */ }
    try {
      if (typeof window !== "undefined" && window.clarity) {
        window.clarity("event", "session_memory_shown");
      }
    } catch { /* noop — Clarity best-effort */ }
    // Iter XX.270 — "Build Your Gully" narrative (Phase 1). Fire the
    // `gully_learned_something` impression the first time a session
    // memory card renders with a REAL observation. Session-deduped
    // by trackFunnel so re-renders / re-mounts are safe. Not gated
    // by the narrative flag — this is a pure telemetry point that
    // records when a memory-saving moment is visible so cohort
    // analysis works regardless of flag state.
    if (memory && memory.observation) {
      try {
        import("../lib/activationFunnel").then(({ trackFunnel }) => {
          trackFunnel("gully_learned_something", {
            category: memory.category || null,
            has_focus: !!memory.current_focus,
            has_commitment: !!memory.next_practice_commitment,
            plus_preview: !!isPlusPreview,
          });
        }).catch(() => { /* noop */ });
      } catch { /* noop */ }
    }
    // Iter XX.174.1 — Visible My Cricket Creation Moment funnel
    // event. Fires ONLY on the pre-Plus preview surface so the
    // founder dashboard can measure:
    //   my_cricket_preview_seen → plus_cta_clicked
    //                          → checkout_started
    //                          → subscription_started
    if (isPlusPreview) {
      try {
        if (typeof window !== "undefined" && window.clarity) {
          window.clarity("event", "my_cricket_preview_seen");
        }
      } catch { /* noop */ }
      try {
        import("../lib/activationFunnel").then(({ trackFunnel }) => {
          trackFunnel("my_cricket_preview_seen", {
            has_focus: !!(memory && memory.current_focus),
            has_commitment: !!(memory && memory.next_practice_commitment),
            session_number: memory ? memory.session_number : null,
          });
        }).catch(() => { /* noop */ });
      } catch { /* noop */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (alreadyShownToday) {
    return null;
  }

  if (!memory || !memory.observation) return null;

  return (
    <div
      data-testid="session-memory-card"
      data-memory-category={memory.category || "match"}
      className={
        "mt-3 w-full rounded-2xl border border-emerald-900/40 "
        + "bg-gradient-to-br from-emerald-950/30 to-neutral-900/50 "
        + "p-4 sm:p-5 space-y-4"
      }
    >
      {/* Header — Iter XX.144: "This story isn't finished yet."
          Replaces the archival "Session N saved" with a coach's-
          notebook opener. Present-tense, warm, in-progress. */}
      <div
        data-testid="session-memory-header"
        className="flex items-center gap-2 text-[13px] font-medium text-emerald-300"
      >
        <Check size={16} className="shrink-0" strokeWidth={2.5} />
        <span data-testid="session-memory-header-text">
          {isPlusPreview
            ? "Added to My Cricket"
            : "Saved to Your Cricket"}
        </span>
      </div>

      {/* Iter XX.288 — Explicit arc-complete moment. Renders exactly
          once, on the same turn the coaching arc closes (this card is
          server-attached only then) and only in the pre-Plus flow so
          it doesn't dilute the confirmation card for existing
          subscribers.
          Uses `current_focus` — the finding/problem the user was
          working on — where available so the moment is grounded in
          the user's own cricket rather than generic filler. If focus
          is missing, falls back to a category-aware phrase.
          Purpose (product): make the free→Plus boundary explicit so
          the in-chat Plus upsell on the NEXT turn feels like a
          continuation of a completed coaching arc, not an interruption. */}
      {!isPlusPreview && (
        <div
          data-testid="session-memory-arc-complete"
          data-arc-complete-has-focus={memory.current_focus ? "true" : "false"}
          className="rounded-xl border border-emerald-900/40 bg-emerald-950/30 px-3.5 py-3 space-y-1.5"
        >
          <div className="text-[12px] uppercase tracking-[0.14em] text-emerald-400/80 font-medium">
            Your first coaching arc is complete
          </div>
          <p className="text-[14px] leading-[1.55] text-neutral-200">
            {memory.current_focus
              ? <>We&rsquo;ve saved what you&rsquo;re working on: <span className="text-emerald-200">{memory.current_focus}</span>.</>
              : <>We&rsquo;ve saved what you&rsquo;re working on.</>}
            {" "}
            Gully Plus keeps building on this across your next matches, practices, videos and conversations.
          </p>
        </div>
      )}

      {/* Current observation — grounded in what Gully actually said
          during the arc. Never fabricated (backend returns null
          → whole card is skipped). */}
      <div
        data-testid="session-memory-observation-block"
        className="space-y-1.5"
      >
        <div
          data-testid="session-memory-observation-label"
          className="text-[11px] uppercase tracking-[0.14em] text-neutral-500"
        >
          {isPlusPreview ? "What we found" : "What I’m noticing"}
        </div>
        <p
          data-testid="session-memory-observation-text"
          className="text-[15px] leading-[1.55] text-neutral-100"
        >
          {`• ${memory.observation}`}
        </p>
      </div>

      {/* Iter XX.144 — "Early read" replaces "Confidence". Same fact,
          coach voice. A doctor never says "50% confident after visit
          1" — they say "I have a working theory." That's what we want
          the player to feel here. */}
      <div
        data-testid="session-memory-confidence-block"
        className="space-y-1.5 border-t border-neutral-800/60 pt-3"
      >
        <div
          data-testid="session-memory-confidence-label"
          className="text-[11px] uppercase tracking-[0.14em] text-neutral-500"
        >
          Early read
        </div>
        <p
          data-testid="session-memory-confidence-line"
          className="text-[14px] leading-snug text-neutral-200"
        >
          {memory.confidence_line || "One session in. Here’s what I’m watching for."}
        </p>
        <p
          data-testid="session-memory-confidence-note"
          className="text-[13px] leading-snug text-neutral-400 italic"
        >
          {memory.confidence_note
            || "I’ll only know for sure after we see this show up (or not) in your next session."}
        </p>
      </div>

      {/* Iter XX.116/144 — Next-practice commitment + return ritual.
          Only rendered when the coach's closing turn produced a
          concrete action. XX.144 appends a coach-voice return ritual
          so the player leaves with a specific appointment, not a
          wrap-up. */}
      {memory.next_practice_commitment && (
        <div
          data-testid="session-memory-commitment-block"
          className="space-y-1.5 border-t border-neutral-800/60 pt-3"
        >
          <div
            data-testid="session-memory-commitment-label"
            className="text-[11px] uppercase tracking-[0.14em] text-neutral-500"
          >
            {isPlusPreview ? "Your next step" : "Before your next session"}
          </div>
          <p
            data-testid="session-memory-commitment-text"
            className="text-[14px] leading-snug text-neutral-100"
          >
            {memory.next_practice_commitment}
          </p>
          {!isPlusPreview && (
            <p
              data-testid="session-memory-return-ritual"
              className="text-[13px] leading-snug text-emerald-300/85 italic"
            >
              Come back after your next match and tell me what happened. That’s
              how we’ll know if we’re seeing the real pattern.
            </p>
          )}
        </div>
      )}

      {/* Iter XX.174.1 — Preview-mode ONLY. Current focus surfaced
          from active_arc.current.topic; grounded field only.
          Renders after the commitment row so the artifact reads:
          What we found → Your next step → Current focus. Absent
          when backend didn't supply a topic — no fabrication. */}
      {isPlusPreview && memory.current_focus && (
        <div
          data-testid="session-memory-current-focus-block"
          className="space-y-1.5 border-t border-neutral-800/60 pt-3"
        >
          <div
            data-testid="session-memory-current-focus-label"
            className="text-[11px] uppercase tracking-[0.14em] text-neutral-500"
          >
            Current focus
          </div>
          <p
            data-testid="session-memory-current-focus-text"
            className="text-[14px] leading-snug text-neutral-100"
          >
            {memory.current_focus}
          </p>
        </div>
      )}

      {/* Iter XX.174.1 — Locked-continuation tail.
          Only in preview mode. Not a CTA, not clickable — the Plus
          offer that follows on its own surface remains the ONE
          actionable commercial element. The tail simply makes it
          visible that today's coaching was saved and could keep
          building through future sessions. */}
      {isPlusPreview && (
        <div
          data-testid="session-memory-plus-tail"
          className="border-t border-neutral-800/60 pt-3 flex items-start gap-2"
        >
          <Lock size={14} className="shrink-0 mt-0.5 text-neutral-400" strokeWidth={2} />
          <div className="space-y-1">
            <p
              data-testid="session-memory-plus-tail-headline"
              className="text-[13.5px] leading-snug text-neutral-200"
            >
              Keep building My Cricket with Gully Plus
            </p>
            <p
              data-testid="session-memory-plus-tail-body"
              className="text-[12.5px] leading-snug text-neutral-500"
            >
              Gully can carry this through your next nets, matches and videos.
            </p>
          </div>
        </div>
      )}

      {/* Iter XX.276 — "Take this into your next innings" confirmation
          moment. Only renders when the backend has attached a
          `loop_candidate` block. Backend gates on
          `active_development_loop_v1_enabled`; absent = zero
          user-visible change. Never uses internal terminology
          ("Active Development Loop", "mission", "loop", "candidate")
          on-screen — the copy is outcome-first. */}
      {memory.loop_candidate && memory.loop_candidate.focus_text ? (
        <DevLoopConfirmModule
          candidate={memory.loop_candidate}
          onAccept={memory.loop_candidate.onAccept}
          onDecline={memory.loop_candidate.onDecline}
        />
      ) : null}

      {/* Iter XX.144 — Timeline of upcoming sessions. Suppressed in
          XX.174.1 preview mode: the lock-tail communicates continuity
          more honestly than empty placeholders when the value is
          about to be paywalled. */}
      {!isPlusPreview && (() => {
        const currentSession = Number(memory.session_number) || 1;
        const upcoming = [1, 2, 3].map((offset) => currentSession + offset);
        return (
          <div
            data-testid="session-memory-timeline-block"
            className="space-y-2 border-t border-neutral-800/60 pt-3"
          >
            <div
              data-testid="session-memory-timeline-header"
              className="text-[11px] uppercase tracking-[0.14em] text-neutral-500"
            >
              Next thing I’ll be watching
            </div>
            <ul
              data-testid="session-memory-timeline-list"
              className="space-y-1.5"
            >
              <li
                data-testid={`session-memory-timeline-item-${currentSession}`}
                data-status="current"
                className="flex items-center gap-2 text-[13.5px] leading-snug text-neutral-100"
              >
                <Check size={14} className="shrink-0 text-emerald-400" strokeWidth={2.5} />
                <span>Session {currentSession} — today</span>
              </li>
              {upcoming.map((n) => (
                <li
                  key={n}
                  data-testid={`session-memory-timeline-item-${n}`}
                  data-status="upcoming"
                  className="flex items-center gap-2 text-[13.5px] leading-snug text-neutral-500"
                >
                  <span
                    aria-hidden="true"
                    className="shrink-0 inline-block w-3.5 h-3.5 rounded-full border border-neutral-700"
                  />
                  <span>Session {n}</span>
                  <span aria-hidden="true" className="text-neutral-700">—</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })()}
    </div>
  );
}


/**
 * Iter XX.276 — DevLoopConfirmModule.
 *
 * The "take this into your next innings" confirmation moment.
 * Rides on the existing SessionMemoryCard so the ask feels like a
 * natural conclusion to the coaching arc rather than a system prompt.
 *
 * Contract (backend `memory.loop_candidate`):
 *   {
 *     focus_text:           "Once you reach 30, next 12 balls along the ground.",
 *     problem:              "Turn your starts into bigger innings." | null,
 *     next_moment:          "next_innings" | ... | null,
 *     player_originated:    bool | undefined,
 *     onAccept:             () => void,
 *     onDecline:            () => void,
 *   }
 *
 * User-facing copy NEVER uses internal terminology
 * ("Active Development Loop", "mission", "loop", "candidate",
 *  "tracking architecture", "experiment state").
 */
function DevLoopConfirmModule({ candidate, onAccept, onDecline }) {
  const shownRef = useRef(false);
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    try {
      import("../lib/activationFunnel").then(({ trackFunnel }) => {
        trackFunnel("development_loop_confirmation_shown", {
          next_moment: candidate?.next_moment || null,
          player_originated: !!candidate?.player_originated,
        });
      }).catch(() => { /* noop */ });
    } catch { /* noop */ }
  }, [candidate?.next_moment, candidate?.player_originated]);

  const label = ({
    next_match:            "Take this into your next match",
    next_innings:          "Take this into your next innings",
    next_net:              "Take this into your next net",
    next_practice:         "Take this into your next practice",
    next_bowling_session:  "Take this into your next bowling session",
    next_spell:            "Take this into your next spell",
    next_video:            "Take this into your next video",
    trials:                "Take this into your trials",
    selection_session:     "Take this into your selection session",
    unknown:               "Take this into your next time out",
  })[candidate?.next_moment] || (
    candidate?.player_originated
      ? "Your plan for next time"
      : "Take this into your next session"
  );

  const handleAccept = () => {
    try {
      import("../lib/activationFunnel").then(({ trackFunnel }) => {
        trackFunnel("development_loop_confirmation_accepted", {
          next_moment: candidate?.next_moment || null,
          player_originated: !!candidate?.player_originated,
        });
      }).catch(() => { /* noop */ });
    } catch { /* noop */ }
    if (typeof onAccept === "function") onAccept();
  };
  const handleDecline = () => {
    try {
      import("../lib/activationFunnel").then(({ trackFunnel }) => {
        trackFunnel("development_loop_confirmation_declined", {
          next_moment: candidate?.next_moment || null,
        });
      }).catch(() => { /* noop */ });
    } catch { /* noop */ }
    if (typeof onDecline === "function") onDecline();
  };

  return (
    <div
      data-testid="dev-loop-confirm-module"
      data-player-originated={candidate?.player_originated ? "true" : "false"}
      className="space-y-3 border-t border-neutral-800/60 pt-3"
    >
      <div
        data-testid="dev-loop-confirm-label"
        className="text-[11px] uppercase tracking-[0.14em] text-emerald-300"
      >
        {label}
      </div>
      <p
        data-testid="dev-loop-confirm-focus-text"
        className="text-[15px] leading-snug text-neutral-100"
      >
        {candidate.focus_text}
      </p>
      <div className="flex flex-col gap-2 pt-1">
        <button
          type="button"
          onClick={handleAccept}
          data-testid="dev-loop-confirm-yes"
          className="w-full inline-flex items-center justify-center py-2.5 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors"
        >
          Track this with Gully
        </button>
        <button
          type="button"
          onClick={handleDecline}
          data-testid="dev-loop-confirm-no"
          className="w-full inline-flex items-center justify-center py-2 px-4 rounded-lg text-neutral-400 hover:text-neutral-200 text-[13px] transition-colors"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
